import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Users, FileText, CreditCard, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    clients: 0,
    requests: 0,
    payments: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [clientsRes, requestsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats({
        clients: clientsRes.data.clients.length,
        requests: requestsRes.data.requests.length,
        payments: paymentsRes.data.payments.filter(p => p.payment_status === 'paid').length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712]">
        <ClientNav />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="admin-dashboard">
      <ClientNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="admin-dashboard-title">Admin Dashboard</h1>
          <p className="text-slate-400">Overview of your agency</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-clients">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <Users className="text-indigo-400" size={20} />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{stats.clients}</h3>
            <p className="text-sm text-slate-400">Total Clients</p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-requests">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <FileText className="text-purple-400" size={20} />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{stats.requests}</h3>
            <p className="text-sm text-slate-400">Total Requests</p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-payments">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CreditCard className="text-green-400" size={20} />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{stats.payments}</h3>
            <p className="text-sm text-slate-400">Paid Invoices</p>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6" data-testid="stat-revenue">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <TrendingUp className="text-cyan-400" size={20} />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">+23%</h3>
            <p className="text-sm text-slate-400">Growth</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
